import { dashboardsAPI, metricsAPI, datasetsAPI, alertsAPI } from '../services/api';
import useStore from '../store/useStore';

/**
 * Shared utility for loading dashboard and metrics data in parallel.
 * Replaces the duplicated sequential loading logic in App.jsx,
 * CompanySelector.jsx, and DashboardSelector.jsx.
 *
 * Core guarantee: widgets never render until both dashboard definition
 * AND metrics data are in the store (gated by dataReady flag).
 *
 * @param {Object} options
 * @param {number} options.companyId - Company to load data for
 * @param {number} [options.dashboardId] - Specific dashboard to load (optional, uses default/first)
 * @param {Array} [options.dashboards] - Pre-loaded dashboards array (skips fetch if provided)
 * @returns {Promise<Object>} { dashboards, dashboard, metrics } loaded data
 */
export async function loadDashboardData({ companyId, dashboardId = null, dashboards = null }) {
  if (!companyId) return null;

  const store = useStore.getState();

  // Gate rendering immediately
  store.setDataReady(false);

  // Increment load counter to invalidate any in-flight HTTP responses
  const thisLoadId = store.incrementMetricsLoadId();

  try {
    // Step 1: Get dashboards list (use provided or fetch)
    let companyDashboards = dashboards;
    if (!companyDashboards) {
      const dashboardsResponse = await dashboardsAPI.getAll();
      if (dashboardsResponse.success) {
        companyDashboards = dashboardsResponse.data.filter(d => d.company_id === companyId);
      } else {
        companyDashboards = [];
      }
    }

    // Update dashboards list in store
    useStore.getState().setDashboards(companyDashboards);

    // Determine which dashboard to load
    let targetDashboard = null;
    if (dashboardId) {
      targetDashboard = companyDashboards.find(d => d.id === parseInt(dashboardId));
    }
    if (!targetDashboard && companyDashboards.length > 0) {
      targetDashboard = companyDashboards.find(d => d.is_default) || companyDashboards[0];
    }

    if (!targetDashboard) {
      // No dashboards available for this company
      useStore.getState().setCurrentDashboard(null);
      useStore.getState().setMetrics({});
      return { dashboards: companyDashboards, dashboard: null, metrics: {} };
    }

    // Step 2: Fetch dashboard details and metrics IN PARALLEL
    const [fullDashboardResponse, metricsResponse, datasetsResponse, alertsResponse] = await Promise.all([
      dashboardsAPI.getById(targetDashboard.id),
      metricsAPI.getLatestAll(companyId),
      datasetsAPI.list(companyId).catch(() => null),
      alertsAPI.active(companyId).catch(() => null)
    ]);

    // Check if this load is still current (a newer load may have started)
    if (useStore.getState().metricsLoadId !== thisLoadId) {
      return null; // Superseded by a newer load, discard
    }

    // Step 3: Process metrics with timestamp-aware merge
    const metricsMap = {};
    let mostRecentTimestamp = null;

    if (metricsResponse.success) {
      const currentMetrics = useStore.getState().metrics;

      metricsResponse.data.forEach((metric) => {
        const existing = currentMetrics[metric.metric_key];

        // Keep fresher WebSocket data if it arrived during the HTTP fetch
        if (existing && existing.timestamp &&
            new Date(existing.timestamp) > new Date(metric.timestamp)) {
          metricsMap[metric.metric_key] = existing;
        } else {
          metricsMap[metric.metric_key] = metric;
        }

        // Track most recent timestamp
        if (metric.timestamp) {
          const metricTime = new Date(metric.timestamp);
          if (!mostRecentTimestamp || metricTime > mostRecentTimestamp) {
            mostRecentTimestamp = metricTime;
          }
        }
      });
    }

    // Step 4: Commit everything to store atomically
    const fullDashboard = fullDashboardResponse.success ? fullDashboardResponse.data : null;

    // Dataset freshness feeds the unified dashboard status (overdue datasets).
    const datasetFreshness = {};
    if (datasetsResponse?.success) {
      for (const d of datasetsResponse.data) datasetFreshness[d.name] = d.freshness;
    }

    useStore.setState({
      metrics: metricsMap,
      currentDashboard: fullDashboard,
      currentDashboardId: fullDashboard?.id,
      datasetFreshness,
      alerts: alertsResponse?.success ? alertsResponse.data : [],
      dataReady: true
    });

    if (fullDashboard?.id) {
      localStorage.setItem('currentDashboardId', fullDashboard.id);
    }

    // Set dashboard last update timestamp
    if (mostRecentTimestamp && fullDashboard?.id) {
      useStore.getState().setDashboardLastUpdate(fullDashboard.id, mostRecentTimestamp);
    }

    return { dashboards: companyDashboards, dashboard: fullDashboard, metrics: metricsMap };
  } catch (error) {
    console.error('Error loading dashboard data:', error);
    // Ensure UI isn't stuck in loading state even on error
    if (useStore.getState().metricsLoadId === thisLoadId) {
      useStore.getState().setDataReady(true);
    }
    throw error;
  }
}
